import { useEffect } from "react"

/**
 * Dismisses inspection when the user clicks outside the table container or
 * the floating FormulaBar.
 *
 * Why this exists as a hook: the `data-formula-bar` marker convention is
 * owned by `FormulaBar` itself; the listener that respects it belongs in
 * the same module so neither side drifts from the other.
 *
 * No-op when `isActive` is false — listener is only attached during an
 * active inspection.
 */
export function useDismissOnOutsideClick({
  isActive,
  containerRef,
  onDismiss,
}: {
  isActive: boolean
  containerRef: React.RefObject<HTMLElement | null>
  onDismiss: () => void
}) {
  useEffect(() => {
    if (!isActive) return
    const handler = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null
      const insideContainer = containerRef.current?.contains(target ?? null) ?? false
      const insideFormulaBar = !!target?.closest("[data-formula-bar]")
      if (!insideContainer && !insideFormulaBar) onDismiss()
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [isActive, containerRef, onDismiss])
}
